import axios from "axios";

const defaultAxiosConfig = (accessToken) => ({
  headers: {
    Authorization: accessToken,
    "Content-Type": "application/json;charset=UTF-8",
  },
});

export const DEFAULT_INSURANCE_PAYERS = [
  {
    partnerPayerId: "PAY-AET-001",
    partner_payer_id: "PAY-AET-001",
    name: "Aetna Better Health of Texas",
    code: "AETNA-TX",
    type: "Private",
    active: true,
  },
  {
    partnerPayerId: "PAY-UHC-002",
    partner_payer_id: "PAY-UHC-002",
    name: "UnitedHealthcare Community Plan",
    code: "UHC-COMM",
    type: "Private",
    active: true,
  },
];

class CustomerApi {
  async getCustomers(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();
    // Add parameters to queryParams only if they are defined
    if (params.offset) queryParams.append("offset", params.offset);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.userStatus) queryParams.append("userStatus", params.userStatus);
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.filterBySaleRepId)
      queryParams.append("filterBySaleRepId", params.filterBySaleRepId);
    if (params.userSubStatus)
      queryParams.append("userSubStatus", params.userSubStatus);
    if (params.ketaminePack)
      queryParams.append("ketaminePack", params.ketaminePack);
    if (params.genPsych) queryParams.append("genPsych", params.genPsych);
    if (params.traumaPack) queryParams.append("traumaPack", params.traumaPack);
    if (params.naltrexonePack)
      queryParams.append("naltrexonePack", params.naltrexonePack);
    if (params.iopPack) queryParams.append("iopPack", params.iopPack);
    if (params.insuranceCustomer)
      queryParams.append("insuranceCustomer", params.insuranceCustomer);
    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/customers/listV2?${queryParams.toString()}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerNotifications(accessToken, customerId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/notifications/user/${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerMemories(accessToken, customerId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/aurora/memories/${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async deleteCustomerMemory(accessToken, customerId, memoryId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/aurora/memories/${customerId}/${memoryId}`;
    const resp = await axios.delete(apiPath, axiosConfig);
    return resp.data;
  }

  async getPatientSummary(customerId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${customerId}/patient-summary`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerFormSubmissions(customerId, latestOnly = false) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/forms?userId=${customerId}&latestOnly=${latestOnly}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getHeightWeightBmi(customerId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/forms/height-weight-bmi?userId=${customerId}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error('Error fetching height/weight/BMI:', error);
      throw error;
    }
  }

  async getInsuranceCollectionsList(daysToAppointment = 20) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/collections?daysToAppointment=${daysToAppointment}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getSubscriptionRefillsList() {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/subscriptions/refill-dates`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getNaxRefillsList(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add product_type parameter if provided
    if (params.product_type) {
      queryParams.append("product_type", params.product_type);
    }

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/nax-refills${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getNaxRefills(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add product_type parameter if provided
    if (params.product_type) {
      queryParams.append("product_type", params.product_type);
    }

    // Add search parameter if provided
    if (params.searchValue) {
      queryParams.append("searchValue", params.searchValue);
    }

    if (params.has_credits) {
      queryParams.append("has_credits", "true");
    }

    if (params.refill_date_start) {
      queryParams.append("refill_date_start", params.refill_date_start);
    }

    if (params.refill_date_end) {
      queryParams.append("refill_date_end", params.refill_date_end);
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/prescriptions/refills${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getNaxNewPrescriptions(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add product_type parameter if provided
    if (params.product_type) {
      queryParams.append("product_type", params.product_type);
    }

    // Add search parameter if provided
    if (params.searchValue) {
      queryParams.append("searchValue", params.searchValue);
    }

    if (params.has_credits) {
      queryParams.append("has_credits", "true");
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/prescriptions/new${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getNaxPausedSubscriptions(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add product_type parameter if provided
    if (params.product_type) {
      queryParams.append("product_type", params.product_type);
    }

    // Add search parameter if provided
    if (params.searchValue) {
      queryParams.append("searchValue", params.searchValue);
    }

    if (params.has_credits) {
      queryParams.append("has_credits", "true");
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/prescriptions/paused${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateNaltrexoneRefill(data) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/subscriptions/refill-updates`;
    const resp = await axios.put(apiPath, JSON.stringify(data), axiosConfig);
    return resp.data;
  }

  async getSalesRepresentative() {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/sales-perosnal/list`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async fetchCustomerByStatus(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/list-by-status`;

    // Ensure status is always present in the params
    if (!params.status) {
      params.status = "lead"; // Default status if not provided
    }

    // Convert boolean values to strings
    const preparedParams = Object.entries(params).reduce(
      (acc, [key, value]) => {
        acc[key] = typeof value === "boolean" ? value.toString() : value;
        return acc;
      },
      {}
    );

    try {
      const resp = await axios.post(apiPath, preparedParams, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching customers by status:", error);
      throw error;
    }
  }

  async updateCustomerStatus(customerId, status, reason) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/customers/change-status`,
      {
        user_status: status,
        user_status_reason: reason,
        id: customerId,
      },
      axiosConfig
    );
    return resp.data;
  }

  async getBIOrders(accessToken) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/bi/orders`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getBIFacebook(accessToken) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/bi/facebook`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomer(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async previewNonMedicalData(customerId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${customerId}/non-medical-data`;
    const resp = await axios.get(apiPath, defaultAxiosConfig(accessToken));
    return resp.data;
  }

  async deleteNonMedicalData(customerId, body) {
    const accessToken = window.localStorage.getItem("accessToken");
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${customerId}/non-medical-data`;
    const resp = await axios.post(apiPath, body, defaultAxiosConfig(accessToken));
    return resp.data;
  }

  async getMedicalProfile(accessToken, userId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/users/${userId}/medical-profile`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateMedicalProfile(accessToken, userId, body) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/users/${userId}/medical-profile`;
    const resp = await axios.put(apiPath, body, axiosConfig);
    return resp.data;
  }

  async getMedicalProfileLogs(accessToken, userId, limit = 50, offset = 0) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/users/${userId}/medical-profile/logs?limit=${limit}&offset=${offset}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getTodos(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/todos-v3?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateTodos(accessToken, customerId, todos) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/todos?customerId=${customerId}`;
    const resp = await axios.post(apiPath, todos, axiosConfig);
    return resp.data;
  }

  async sendExpeditedShippingCheckoutEmail(accessToken, purchaseId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/purchases/${purchaseId}/expedited-shipping-email`;
    const resp = await axios.post(apiPath, {}, axiosConfig);
    return resp.data;
  }

  async getPurchases(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/purchases?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getSubscriptionCreditPurchases(accessToken, userId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/purchases/subscription-credits?userId=${userId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updatePrescriptionPurchase(accessToken, prescriptionId, purchaseId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/prescriptions/${prescriptionId}/purchase`;
    const resp = await axios.put(apiPath, { purchaseId }, axiosConfig);
    return resp.data;
  }

  async linkPrescriptionToConsultation(
    accessToken,
    prescriptionId,
    { consultationId, linkGroup = false } = {}
  ) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/prescriptions/${prescriptionId}/consultation`;
    const resp = await axios.put(
      apiPath,
      { consultationId, linkGroup },
      axiosConfig
    );
    return resp.data;
  }

  async updatePrescriptionTrackingNumber(
    accessToken,
    prescriptionId,
    { orderId, oldTrackingNumber, newTrackingNumber },
  ) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/prescriptions/${prescriptionId}/tracking`;
    const resp = await axios.put(
      apiPath,
      { orderId, oldTrackingNumber, newTrackingNumber },
      axiosConfig,
    );
    return resp.data;
  }

  async getAllPurchases(params) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Use page directly
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add other parameters
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.productType)
      queryParams.append("productType", params.productType);
    if (params.purchaseStatus)
      queryParams.append("purchaseStatus", params.purchaseStatus);
    if (params.isInsurancePurchase !== null)
      queryParams.append("isInsurancePurchase", params.isInsurancePurchase);
    if (params.userStatus) queryParams.append("userStatus", params.userStatus);
    if (params.statusReason)
      queryParams.append("statusReason", params.statusReason);
    if (params.userState) queryParams.append("userState", params.userState);
    if (params.isFirstPurchase !== null)
      queryParams.append("isFirstPurchase", params.isFirstPurchase);
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);
    if (params.showTestUsers)
      queryParams.append("showTestUsers", params.showTestUsers);
    if (params.therapyPreference)
      queryParams.append("therapyPreference", params.therapyPreference);
    if (params.successManagerId)
      queryParams.append("successManagerId", params.successManagerId);
    if (
      params.confirmedOnboardingCompletion === "true" ||
      params.confirmedOnboardingCompletion === "false"
    ) {
      queryParams.append(
        "confirmedOnboardingCompletion",
        params.confirmedOnboardingCompletion
      );
    }
    if (params.isZeroInitialPlan !== null && params.isZeroInitialPlan !== undefined) {
      queryParams.append("isZeroInitialPlan", params.isZeroInitialPlan);
    }
    if (params.has_coaching !== null && params.has_coaching !== undefined) {
      queryParams.append("has_coaching", params.has_coaching);
    }
    if (params.subscriptionStatus) {
      queryParams.append("subscriptionStatus", params.subscriptionStatus);
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/purchases/list?${queryParams.toString()}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getFormSubmissions(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.formName) queryParams.append("formName", params.formName);
    if (params.formSource) queryParams.append("formSource", params.formSource);
    if (params.productType) queryParams.append("productType", params.productType);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.isApproved !== null && params.isApproved !== undefined) {
      queryParams.append("isApproved", params.isApproved);
    }
    if (params.searchValue) {
      queryParams.append("searchValue", params.searchValue);
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/form-submissions${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getIopLeads(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.source) queryParams.append("source", params.source);
    if (params.status) queryParams.append("status", params.status);
    if (params.pipeline) queryParams.append("pipeline", params.pipeline);
    if (params.isApproved !== null && params.isApproved !== undefined) {
      queryParams.append("isApproved", params.isApproved);
    }
    if (params.searchValue) {
      queryParams.append("searchValue", params.searchValue);
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/iop-leads${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async createIopLead(payload = {}) {
    // Accepts either { userid, notes } for an existing user, or new-user contact
    // fields ({ firstname, lastname, email, phonenumber, stateusa, notes }) that
    // the backend uses to create (or reuse by email) a user, then attach a lead.
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-leads`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async updateIopLead(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-leads/${id}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async sendIopIntakeInvite(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-leads/${id}/send-intake`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async openIopProxyForm(userId, formName, extra = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${userId}/forms/proxy-session`,
      { formName, ...extra },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopEpisodesList(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.status) queryParams.append("status", params.status);
    if (params.pipeline) queryParams.append("pipeline", params.pipeline);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.searchValue) queryParams.append("searchValue", params.searchValue);
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopCohortsList(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.status) queryParams.append("status", params.status);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.searchValue) queryParams.append("searchValue", params.searchValue);
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-cohorts${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async createIopCohort(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-cohorts`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopCohort(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-cohorts/${id}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopCohortCompliance(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/iop-cohorts/${id}/compliance`,
        defaultAxiosConfig(accessToken)
      );
      const payload = resp.data;
      if (payload?.summary || payload?.patients || payload?.cohort_id) {
        return payload;
      }
      if (payload?.data?.summary || payload?.data?.patients || payload?.data?.cohort_id) {
        return payload.data;
      }
      return payload;
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateIopCohort(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-cohorts/${id}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async assignEpisodeCohort(episodeId, cohortId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/cohort`,
      { cohort_id: cohortId },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  // CHO-1608: continue an unfinished patient into a later cohort, scheduling only
  // the curriculum weeks they were never scheduled for before.
  async rollEpisodeIntoCohort(episodeId, cohortId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/roll-cohort`,
      { cohort_id: cohortId },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  // Hard removal of a single patient from one group session. Deliberate and
  // not reversible by the patient, but an admin can re-add via addPatientToSession.
  async removePatientFromSession(patientSessionId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.delete(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-patient-sessions/${patientSessionId}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  // Add a patient back onto a group session in their episode's cohort (to revert
  // a mistaken removal).
  async addPatientToSession(episodeId, sessionId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/sessions`,
      { session_id: sessionId },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  // The episode cohort's group sessions the patient is NOT currently on, for the
  // add-back picker.
  async listAddableIopSessions(episodeId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/addable-sessions`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async listIopSessions(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.cohortId) queryParams.append("cohortId", params.cohortId);
    if (params.episodeId) queryParams.append("episodeId", params.episodeId);
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async createIopSession(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async createIopSessionSchedule(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/schedule`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopIndividualAvailability(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.month) queryParams.append("month", params.month);
    if (params.date) queryParams.append("date", params.date);
    if (params.timezone) queryParams.append("timezone", params.timezone);
    if (params.appointmentTypeID) {
      queryParams.append("appointmentTypeID", params.appointmentTypeID);
    }
    if (params.calendarID) queryParams.append("calendarID", params.calendarID);
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/availability${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async listIopGroupFacilitators() {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-group-facilitators`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopAppointmentCatalog() {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-appointments/catalog`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopAppointmentProviders(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.appointmentTypeID) {
      queryParams.append("appointmentTypeID", params.appointmentTypeID);
    }
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-appointments/providers${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async bookIopAppointment(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-appointments`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async rescheduleIopAppointment(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-appointments/reschedule`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async cancelIopAppointment(payload = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-appointments/cancel`,
      payload,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopSession(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/${id}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async updateIopSession(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/${id}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopSessionAttendance(id, patients = []) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/${id}/attendance`,
      { patients },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async signIopSessionNote(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-sessions/${id}/session-note/sign`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopPatientSession(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-patient-sessions/${id}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async updateIopPatientSession(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-patient-sessions/${id}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async signIopPatientSessionSoap(id, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-patient-sessions/${id}/soap/sign`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopChart({ customerId, episodeId } = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (episodeId) queryParams.append("episodeId", episodeId);
    if (customerId) queryParams.append("customerId", customerId);
    const qs = queryParams.toString();
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-chart${qs ? `?${qs}` : ""}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async ensureIopEpisode(customerId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes`,
      { customerId },
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopAdmissionNote(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/admission-note`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async signIopAdmissionNote(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/admission-note/sign`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async replaceIopAdmissionSignature(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/admission-note/signature`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async attestIopAdmission(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/admission-note/attest`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopAdmissionNotePdf(episodeId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/admission-note/pdf`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async activateIopEpisode(episodeId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/activate`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async dischargeIopEpisode(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/discharge`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopChartDocument(episodeId, documentType, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/chart-documents/${documentType}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async signIopChartDocument(episodeId, documentType, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/chart-documents/${documentType}/sign`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async reassessIopChartDocument(episodeId, documentType) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/chart-documents/${documentType}/reassess`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopChartDocumentPdf(episodeId, documentType, documentId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const qs = documentId ? `?document_id=${encodeURIComponent(documentId)}` : "";
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/chart-documents/${documentType}/pdf${qs}`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async createIopEpisodeNote(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/episode-notes`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopEpisodeNote(episodeId, noteId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/episode-notes/${noteId}`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async signIopEpisodeNote(episodeId, noteId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/episode-notes/${noteId}/sign`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopEpisodeNotePdf(episodeId, noteId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/episode-notes/${noteId}/pdf`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopTreatmentPlan(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/treatment-plan`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async reviewIopTreatmentPlan(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/treatment-plan/review`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async startIopTreatmentPlanAddendum(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/treatment-plan/addendum`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async saveIopDischargePlan(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/discharge-plan`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async reviewIopDischargePlan(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/discharge-plan/review`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async startIopDischargePlanAddendum(episodeId, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/discharge-plan/addendum`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async shareIopWopPacket(episodeId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/signable-documents/share-wop-packet`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async shareIopSignableDocument(episodeId, documentType, body = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/signable-documents/${documentType}/share`,
      body,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async unlockIopSignableDocument(episodeId, documentId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/signable-documents/${documentId}/unlock`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async relockIopSignableDocument(episodeId, documentId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/signable-documents/${documentId}/relock`,
      {},
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getIopSignableDocumentPdf(episodeId, documentId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const resp = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/admin/iop-episodes/${episodeId}/signable-documents/${documentId}/pdf`,
      defaultAxiosConfig(accessToken)
    );
    return resp.data;
  }

  async getSupportGroupSurveys() {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "GET",
      url: `${process.env.REACT_APP_BACKEND_URL}/forms/support-group`,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async getUxSurveys() {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "GET",
      url: `${process.env.REACT_APP_BACKEND_URL}/forms/ux-survey`,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async getNpsSurveys() {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "GET",
      url: `${process.env.REACT_APP_BACKEND_URL}/forms/nps-overview`,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async getAllLeads(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    // Build query string based on params
    const queryParams = new URLSearchParams();
    // Append only the params that are passed in
    if (params.offset) queryParams.append("offset", params.offset);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);
    if (params.productType)
      queryParams.append("productType", params.productType);
    if (params.formSource) queryParams.append("formSource", params.formSource);
    if (params.formName) queryParams.append("formName", params.formName);
    if (
      params.hasPassedLastAssessment !== null &&
      params.hasPassedLastAssessment !== undefined
    ) {
      queryParams.append(
        "hasPassedLastAssessment",
        params.hasPassedLastAssessment.toString()
      );
    }
    if (params.state) queryParams.append("state", params.state);
    if (params.salesOwner) queryParams.append("salesOwner", params.salesOwner);
    if (
      params.hasInsuranceDetails !== null &&
      params.hasInsuranceDetails !== undefined
    ) {
      queryParams.append("hasInsurance", params.hasInsuranceDetails.toString());
    }
    if (params.searchValue) queryParams.append("search", params.searchValue);

    // Construct the API path
    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/leads?${queryParams.toString()}`;

    try {
      // Make the API request
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching leads:", error);
      throw error;
    }
  }

  async updatePurchase(accessToken, purchaseId, update) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/purchases?purchaseId=${purchaseId}`;
    const resp = await axios.post(apiPath, update, axiosConfig);
    return resp.data;
  }

  async updatePackComplete(accessToken, purchaseId, update) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/purchases/complete?purchaseId=${purchaseId}`;
    const resp = await axios.post(apiPath, update, axiosConfig);
    return resp.data;
  }

  async getPrescriptionTracking(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/prescription-tracking?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerPaymentsSqaureup(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customer-payments-sqaureup?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerSubscriptionsSqaureup(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customer-subscriptions-sqaureup?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerSubscriptionsCancelSqaureup(accessToken, subId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customer-subscriptions-cancel-sqaureup?subId=${subId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async refundCustomerSquareup(accessToken, customerId, paymentId, amount) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customer-refunds-sqaureup?customerId=${customerId}&paymentId=${paymentId}&amount=${amount}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateCustomer(accessToken, customerId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers?customerId=${customerId}`;
    const resp = await axios.put(apiPath, data, axiosConfig);
    return resp.data;
  }

  async updateInsuranceEligibilityDetails(accessToken, customerId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/insurance?userId=${customerId}`;
    const resp = await axios.put(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getPatientInsurances(accessToken, customerId, includeArchived = false) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();
    queryParams.append("user_id", customerId);
    queryParams.append("include_archived", includeArchived ? "true" : "false");

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/patient-insurances?${queryParams.toString()}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getInsurancePayers(accessToken, partner = "neuralrev") {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();
    queryParams.append("partner", partner);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/payers?${queryParams.toString()}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to fetch insurance payers, using default payer list:", error);
      return DEFAULT_INSURANCE_PAYERS.map((payer) => ({ ...payer }));
    }
  }

  async createPatientInsurance(accessToken, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/patient-insurances`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async updatePatientInsurance(accessToken, patientInsuranceId, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/patient-insurances/${patientInsuranceId}`;
    const resp = await axios.patch(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getInsuranceProviders(accessToken, partner = "neuralrev") {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();
    queryParams.append("partner", partner);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/providers?${queryParams.toString()}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to fetch insurance providers:", error);
      return [
        { providerId: "1765432194", name: "Dana Testdoc", npi: "1765432194" },
      ];
    }
  }

  async getEligibilityChecks(accessToken, patientInsuranceId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/eligibility-checks?patient_insurance_id=${patientInsuranceId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async checkPatientEligibility(accessToken, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/eligibility-checks`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getInsuranceEncounters(accessToken, userId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();
    queryParams.append("user_id", userId);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/encounters?${queryParams.toString()}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async upsertInsuranceEncounter(accessToken, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/encounters`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async uploadInsuranceEncounterDocument(accessToken, encounterId, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/encounters/${encounterId}/documents`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async deleteInsuranceEncounterDocument(accessToken, encounterId, documentId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/insurance/encounters/${encounterId}/documents/${documentId}`;
    const resp = await axios.delete(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerPayments(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/payments?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerSubscriptions(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/subscriptions?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async cancelCustomerSubscription(accessToken, subId) {
    const axiosConfig = defaultAxiosConfig(accessToken);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/subscriptions/${subId}/cancel`;
    const resp = await axios.post(apiPath, null, axiosConfig);
    return resp.data;
  }

  async cancelFinancingContract(accessToken, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/financing/contract/cancel`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getCustomerTrackings(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/tracking?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomersDocumentsList(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/documents?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerFormsList(accessToken, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/medical-intake-forms/list?customerId=${customerId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getCustomerFormDetails(accessToken, medicalIntakeId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/medical-intake-form-by-id?medicalIntakeId=${medicalIntakeId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async uploadCustomersDocument(accessToken, customerId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/documents?customerId=${customerId}`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async retagCustomerDocument(accessToken, documentId, tag) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/documents/${documentId}`;
    const resp = await axios.patch(apiPath, { tag }, axiosConfig);
    return resp.data;
  }

  async getS3SignedDocumentLink(accessToken, documentId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/documents/signed-url?documentId=${documentId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async setIdVerification(accessToken, userId, { action, fileId, note } = {}) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${userId}/id-verification`;
    const resp = await axios.post(
      apiPath,
      { action, fileId, note },
      axiosConfig
    );
    return resp.data;
  }

  async getAppointmentLogs(accessToken, customerId, purchaseId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs?customerId=${customerId}&purchaseId=${purchaseId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getAppointmentLog(accessToken, customerId, logId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs?customerId=${customerId}&logId=${logId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateAppointmentLogs(accessToken, customerId, logId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs?customerId=${customerId}&logId=${logId}`;
    const resp = await axios.put(apiPath, data, axiosConfig);
    return resp.data;
  }

  async deleteAppointmentLogs(accessToken, customerId, logId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs?customerId=${customerId}&logId=${logId}`;
    const resp = await axios.delete(apiPath, axiosConfig);
    return resp.data;
  }

  async createAppointmentLogs(accessToken, log) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs`;
    const resp = await axios.post(apiPath, log, axiosConfig);
    return resp.data;
  }

  async refundCustomer(accessToken, chargeId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/payments/refund?id=${chargeId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async refundCustomerWalnut(accessToken, id) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/payments/walnut-refund?id=${id}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getAllCustomers(accessToken) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/list?all=true`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async createHsaDenefitsPurchase(accessToken, customerId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/create-hsa-purchase?customerId=${customerId}`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async createZeroDollarPurchase(accessToken, customerId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/create-zero-dollar-purchase`;
    const resp = await axios.post(
      apiPath,
      { userId: customerId, ...data },
      axiosConfig
    );
    return resp.data;
  }

  async getAsyncConsults(accessToken, params = {}) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    let apiPath = `${process.env.REACT_APP_BACKEND_URL}/async-consultation`;
    const urlParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) urlParams.append("page", params.page);
    if (params.limit) urlParams.append("limit", params.limit);
    if (params.sortBy) urlParams.append("sortBy", params.sortBy);
    if (params.sortDir) urlParams.append("sortDir", params.sortDir);

    // Add filter parameters
    if (params.myConsultsOnly)
      urlParams.append("myConsultsOnly", params.myConsultsOnly);
    if (params.userId) urlParams.append("userId", params.userId);
    if (params.purchaseId) urlParams.append("purchaseId", params.purchaseId);
    if (params.search) urlParams.append("search", params.search);
    if (params.status) urlParams.append("status", params.status);
    if (params.chatStatus) urlParams.append("chatStatus", params.chatStatus);
    if (params.prioritizeNeedsAttention)
      urlParams.append(
        "prioritizeNeedsAttention",
        params.prioritizeNeedsAttention
      );

    if (urlParams.toString()) {
      apiPath += `?${urlParams.toString()}`;
    }

    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async getAsyncConsultation(accessToken, consultationId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/async-consultation/${consultationId}`;

    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async finalizeAsyncConsult(accessToken, consultationId, status, outcome) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/async-consultation/${consultationId}/finalize`;
    const data = {
      status,
      outcome,
    };
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async updateConsultation(accessToken, consultationId, status, outcome, acknowledgedBlockerIds, options = {}) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/consultation/${consultationId}`;
    const data = {
      status,
      ...(outcome ? { outcome } : {}),
      ...(acknowledgedBlockerIds ? { acknowledgedBlockerIds } : {}),
      ...(options.is_silent ? { is_silent: true } : {}),
    };
    const resp = await axios.put(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getChat(accessToken, chatId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}`;
    const resp = await axios.get(apiPath, axiosConfig);
    return resp.data;
  }

  async updateChat(accessToken, chatId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/chat/${chatId}`;
    const resp = await axios.put(apiPath, data, axiosConfig);
    return resp.data;
  }

  async sendMessage(accessToken, chatId, message, attachments = []) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/message`;
    const data = {
      message,
      attachments,
    };
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getFile(accessToken, chatId, fileId, customerId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/user/${customerId}/file/${fileId}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to get file", error);
      throw error;
    }
  }

  async uploadAdminChatFile(accessToken, chatId, fileObject) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/upload`;
    const resp = await axios.post(apiPath, fileObject, axiosConfig);
    return resp.data;
  }

  async uploadAdminChatFiles(accessToken, chatId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/upload`;
    const resp = await axios.post(apiPath, data, axiosConfig);
    return resp.data;
  }

  async getPrescreen(accessToken, patientId, consultType) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    // Scope the prescreen to the consult's program so a different program's prescreen
    // can't source this consult's medical info.
    const typeParam = consultType ? `?consultType=${encodeURIComponent(consultType)}` : '';
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/forms/${patientId}/prescreen${typeParam}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to get prescreen", error);
      throw error;
    }
  }

  async getPatientPrescriptions(accessToken, patientId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/patient/${patientId}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to get patient prescriptions", error);
      throw error;
    }
  }

  async getPatientPrescriptionsWithRefills(accessToken, patientId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/patient/${patientId}/with-refills`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to get patient prescriptions", error);
      throw error;
    }
  }

  async getPrescriptionPrefill(accessToken, purchaseId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${purchaseId}/prefill`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      // Return the full response object which now includes:
      // { options: MedicationOption[], allow_prescription_groups: boolean }
      return resp.data;
    } catch (error) {
      console.error("Error fetching prescription prefill options:", error);
      throw error;
    }
  }

  async createPrescription(accessToken, prescriptionData) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions`;
    try {
      // Ensure the data structure matches exactly what the API expects
      const payload = {
        patientId: prescriptionData.patientId,
        purchaseId: prescriptionData.purchaseId,
        rxQueueId: prescriptionData.rxQueueId,
        service: prescriptionData.service,
        medication: {
          name: prescriptionData.medication.name,
          quantity: prescriptionData.medication.quantity,
          refills: prescriptionData.medication.refills,
          directions: prescriptionData.medication.directions,
          dose_frequency: prescriptionData.medication.dose_frequency,
          units_dose: prescriptionData.medication.units_dose,
        },
      };
      if (prescriptionData.force) {
        payload.force = true;
      }
      const resp = await axios.post(apiPath, payload, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error creating prescription:", error);
      throw error;
    }
  }

  async createAmdPrescription(accessToken, data) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/amd`;
    try {
      const payload = {
        patientId: data.patientId,
        purchaseId: data.purchaseId,
        consultationId: data.consultationId,
        medication: data.medication,
        adjunctMedication: data.adjunctMedication,
        summaryNotes: data.summaryNotes,
        idempotencyId: data.idempotencyId,
      };
      const resp = await axios.post(apiPath, payload, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error creating AMD prescription:", error);
      throw error;
    }
  }

  async confirmAmdPrescription(accessToken, prescriptionId, data = {}) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const doctorPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/amd-confirm`;
    const adminPath = `${process.env.REACT_APP_BACKEND_URL}/admin/prescriptions/${prescriptionId}/amd-confirm`;
    // Back-compat: allow passing external ID as the 3rd arg string
    const payload =
      typeof data === "string"
        ? { externalPrescriptionId: data, status: "written" }
        : {
            status: data.status,
            externalPrescriptionId: data.externalPrescriptionId,
          };
    try {
      const resp = await axios.put(doctorPath, payload, axiosConfig);
      return resp.data;
    } catch (doctorError) {
      if (doctorError?.response?.status === 401 || doctorError?.response?.status === 403) {
        try {
          const resp = await axios.put(adminPath, payload, axiosConfig);
          return resp.data;
        } catch (adminError) {
          console.error("Error updating AMD prescription:", adminError);
          throw adminError;
        }
      }
      console.error("Error updating AMD prescription:", doctorError);
      throw doctorError;
    }
  }

  async createPrescriptionGroup(accessToken, prescriptionGroupData) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/group`;
    try {
      // Group payload has single patientId, purchaseId, service with medications array
      const payload = {
        patientId: prescriptionGroupData.patientId,
        purchaseId: prescriptionGroupData.purchaseId,
        ...(prescriptionGroupData.rxQueueId && {
          rxQueueId: prescriptionGroupData.rxQueueId,
        }),
        service: prescriptionGroupData.service,
        medications: prescriptionGroupData.medications,
      };
      if (prescriptionGroupData.force) {
        payload.force = true;
      }
      const resp = await axios.post(apiPath, payload, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error creating prescription group:", error);
      throw error;
    }
  }

  async createPrescriptionRefill(accessToken, prescriptionId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/refill`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error creating prescription refill:", error);
      throw error;
    }
  }

  async cancelPrescription(accessToken, prescriptionId) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/cancel`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Failed to cancel prescription", error);
      throw error;
    }
  }

  async submitPrescriptionValidationLog(
    accessToken,
    prescriptionId,
    validationLogs
  ) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/validation-log`;
    try {
      const resp = await axios.post(apiPath, { validationLogs }, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error submitting prescription validation log:", error);
      throw error;
    }
  }

  async getSalesNotes(accessToken, userId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/users/${userId}/sales-notes`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching sales notes:", error);
      throw error;
    }
  }

  async deleteSalesNote(accessToken, noteId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/users/sales-notes/${noteId}`;
    try {
      const resp = await axios.delete(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error deleting sales note:", error);
      throw error;
    }
  }

  async createSalesNote(accessToken, userId, note) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/users/sales-notes`;
    try {
      const resp = await axios.post(
        apiPath,
        {
          note,
          user_id: userId,
          user_type: "d2c",
        },
        axiosConfig
      );
      return resp.data;
    } catch (error) {
      console.error("Error creating sales note:", error);
      throw error;
    }
  }

  async toggleUserSuppression(accessToken, userId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/users/${userId}/toggle-suppression`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error toggling user suppression:", error);
      throw error;
    }
  }

  async reactivateMobileAccount(accessToken, userId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/${userId}/reactivate-mobile`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error reactivating mobile account:", error);
      throw error;
    }
  }

  async getPrescriptionDetails(accessToken, prescriptionId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error getting prescription details:", error);
      throw error;
    }
  }

  async queueForRx(
    accessToken,
    patientId,
    purchaseId,
    recommendedProvider = null
  ) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/rx-queue`;
    try {
      const requestBody = {
        patient_id: patientId,
        purchase_id: purchaseId,
      };

      if (recommendedProvider) {
        requestBody.recommended_provider = recommendedProvider;
      }

      const resp = await axios.post(apiPath, requestBody, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error queueing for Rx:", error);
      throw error;
    }
  }

  async handleNewRx(accessToken, prescriptionId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/send`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error handling new Rx:", error);
      throw error;
    }
  }

  async handleGroupShipment(accessToken, groupId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/group/${groupId}/send`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error shipping prescription group:", error);
      throw error;
    }
  }

  async handleRefill(accessToken, prescriptionId, notes) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/${prescriptionId}/refill`;
    try {
      const resp = await axios.post(apiPath, { notes }, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error handling refill:", error);
      throw error;
    }
  }

  async getRxQueue(accessToken) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/rx-queue`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching Rx queue:", error);
      throw error;
    }
  }

  async deleteItemFromRxQueue(accessToken, itemId) {
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/rx-queue/${itemId}`;
    try {
      const resp = await axios.delete(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error deleting item from Rx queue:", error);
      throw error;
    }
  }

  async toggleSubscription(accessToken, subscriptionId, payload = {}) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/subscriptions/${subscriptionId}/toggle`;
    try {
      const resp = await axios.post(apiPath, payload, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error toggling subscription:", error);
      throw error;
    }
  }

  async addTodoStep(accessToken, purchaseId, data) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/todos/purchase/${purchaseId}/step`;
    try {
      const resp = await axios.post(apiPath, data, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error adding todo step:", error);
      throw error;
    }
  }

  async deleteTodoStep(accessToken, purchaseId, stepKey) {
    const axiosConfig = {
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/json;charset=UTF-8",
      },
    };
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/todos/purchase/${purchaseId}/step/${stepKey}`;
    try {
      const resp = await axios.delete(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error deleting todo step:", error);
      throw error;
    }
  }

  async createDoctorRequest(data) {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_BACKEND_URL}/doctor-request`,
      data,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async fulfillDoctorRequest(requestId, data = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_BACKEND_URL}/doctor-request/${requestId}/fulfill`,
      data,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async cancelDoctorRequest(requestId, data = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_BACKEND_URL}/doctor-request/${requestId}/cancel`,
      data,
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async getDoctors() {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/doctor-request/doctors`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching doctors:", error);
      throw error;
    }
  }

  async getDoctorRequests(patientId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/doctor-request?patientId=${patientId}`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching doctor requests:", error);
      throw error;
    }
  }

  async getAllDoctorRequests() {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/doctor-request`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching doctor requests:", error);
      throw error;
    }
  }

  async getTestKits(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add optional parameters if they exist
    if (params.purchaseId) queryParams.append("purchaseId", params.purchaseId);
    if (params.status) queryParams.append("status", params.status);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/test-kits/list${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    try {
      const resp = await axios.get(apiPath, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error fetching test kits:", error);
      throw error;
    }
  }

  async updateTestKit(testKitId, updates) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/test-kits/${testKitId}`;
    try {
      const resp = await axios.put(apiPath, updates, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error updating test kit:", error);
      throw error;
    }
  }

  async retryTestKitShipping(testKitId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/test-kits/${testKitId}/ship`;
    try {
      const resp = await axios.post(apiPath, {}, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error retrying test kit shipping:", error);
      throw error;
    }
  }

  async createTestKit(testKitData) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/test-kits`;
    try {
      const resp = await axios.post(apiPath, testKitData, axiosConfig);
      return resp.data;
    } catch (error) {
      console.error("Error creating test kit:", error);
      throw error;
    }
  }

  async previewTrustpilotInvite({
    email,
    personalNote,
    senderName,
    senderTitle,
    senderFirstName,
  }) {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_BACKEND_URL}/notifications/trustpilot-invites/preview`,
      data: {
        emails: [email],
        invitee_type: "patient",
        personalNote,
        senderName,
        senderTitle,
        senderFirstName,
      },
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async sendTrustpilotInvite({
    email,
    personalNote,
    senderName,
    senderTitle,
    senderFirstName,
    force = false,
  }) {
    const accessToken = window.localStorage.getItem("accessToken");
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_BACKEND_URL}/notifications/trustpilot-invites`,
      data: {
        emails: [email],
        invitee_type: "patient",
        personalNote,
        senderName,
        senderTitle,
        senderFirstName,
        force,
      },
      ...defaultAxiosConfig(accessToken),
    });

    return response.data;
  }

  async getCommunicationCatalog(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);

    const queryParams = new URLSearchParams();
    if (params.userId) {
      queryParams.append("userId", params.userId);
    }

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/notifications/communication-catalog${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching communication catalog:", error);
      throw error;
    }
  }

  // Returns a page of a user's text messages for the chat-style Texts tab.
  // Response shape: { messages: [...] oldest-first, has_more: boolean }.
  // Pass `before` (a message id or ISO timestamp) to page backwards while
  // scrolling up, and `limit` to control page size.
  async getCustomerMessages(accessToken, customerId, { before, limit } = {}) {
    try {
      const apiPath = `${process.env.REACT_APP_BACKEND_URL}/text-messages/${customerId}`;

      const params = {};
      if (before) params.before = before;
      if (limit) params.limit = limit;

      const resp = await axios.get(apiPath, {
        headers: {
          Authorization: accessToken,
          "Content-Type": "application/json",
        },
        params,
      });

      return resp.data;
    } catch (error) {
      console.error("Error in getCustomerMessages:", error);
      throw error;
    }
  }

  async getQueuedCharges(accessToken, userId) {
    try {
      const apiPath = `${process.env.REACT_APP_BACKEND_URL}/subscriptions/users/${userId}/queued-charges`;

      const resp = await axios.get(apiPath, {
        headers: {
          Authorization: accessToken,
          "Content-Type": "application/json",
        },
      });

      return resp.data;
    } catch (error) {
      console.error("Error in getQueuedCharges:", error);
      throw error;
    }
  }

  async cancelQueuedCharge(accessToken, userId, queueItemId) {
    try {
      const apiPath = `${process.env.REACT_APP_BACKEND_URL}/subscription/users/${userId}/queued-charges/${queueItemId}`;

      const resp = await axios.put(
        apiPath,
        { status: "cancel" },
        {
          headers: {
            Authorization: accessToken,
            "Content-Type": "application/json",
          },
        }
      );

      return resp.data;
    } catch (error) {
      console.error("Error in cancelQueuedCharge:", error);
      throw error;
    }
  }

  async cancelAllQueuedCharges(accessToken, userId) {
    try {
      const apiPath = `${process.env.REACT_APP_BACKEND_URL}/subscription/users/${userId}/queued-charges/cancel-all`;

      const resp = await axios.post(
        apiPath,
        {},
        {
          headers: {
            Authorization: accessToken,
            "Content-Type": "application/json",
          },
        }
      );

      return resp.data;
    } catch (error) {
      console.error("Error in cancelAllQueuedCharges:", error);
      throw error;
    }
  }

  async getKetamineSubscriptions(params = {}) {
    try {
      const accessToken = window.localStorage.getItem("accessToken");
      const axiosConfig = defaultAxiosConfig(accessToken);
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.offset !== undefined)
        queryParams.append("offset", params.offset);
      if (params.limit !== undefined) queryParams.append("limit", params.limit);

      // Add sorting parameters
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      // Add search and filter parameters
      if (params.searchValue)
        queryParams.append("searchValue", params.searchValue);
      if (params.status) queryParams.append("status", params.status);
      if (params.isFirstPack !== undefined)
        queryParams.append("isFirstPack", params.isFirstPack);

      const apiPath = `${
        process.env.REACT_APP_BACKEND_URL
      }/admin/list-ketamine-subscriptions${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await axios.get(apiPath, axiosConfig);

      return response.data;
    } catch (error) {
      console.error("Error fetching ketamine subscriptions:", error);
      throw error;
    }
  }

  async getP2PReferrals(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add search and filter parameters
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);
    if (params.productType)
      queryParams.append("productType", params.productType);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortDir) queryParams.append("sortDir", params.sortDir);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/p2p-referrals${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching P2P referrals:", error);
      throw error;
    }
  }

  async getAddictionRecoveryPatients(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Add filter parameters
    if (params.userStatus) queryParams.append("userStatus", params.userStatus);
    if (params.userState) queryParams.append("userState", params.userState);
    if (params.successManagerId)
      queryParams.append("successManagerId", params.successManagerId);
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);
    if (params.coachId) queryParams.append("coachId", params.coachId);
    if (params.subscriptionStatus)
      queryParams.append("subscriptionStatus", params.subscriptionStatus);
    if (
      params.confirmedOnboardingCompletion === "true" ||
      params.confirmedOnboardingCompletion === "false"
    ) {
      queryParams.append(
        "confirmedOnboardingCompletion",
        params.confirmedOnboardingCompletion
      );
    }
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const apiPath = `${
      process.env.REACT_APP_BACKEND_URL
    }/admin/customers/addiction-recovery${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching addiction recovery patients:", error);
      throw error;
    }
  }

  async getAddictionRecoveryPatientDetails(purchaseId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/customers/addiction-recovery/${purchaseId}`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching addiction recovery patient details:",
        error
      );
      throw error;
    }
  }

  async startKetamineJourney(purchaseId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/programs/${purchaseId}/start-ketamine-journey`;

    try {
      const response = await axios.post(apiPath, {}, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error starting ketamine journey:", error);
      throw error;
    }
  }

  async sendDirectFormLink(patientId, data) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/forms/send-form-link-to-patient/${patientId}`;

    try {
      const response = await axios.post(apiPath, data, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error sending direct form link:", error);
      throw error;
    }
  }

  // AI Notes API Methods
  async generateAINotes(logId, data) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs/${logId}/generate-ai-notes`;

    try {
      const response = await axios.post(apiPath, data, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error generating AI notes:", error);
      throw error;
    }
  }

  async getAINotes(logId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs/${logId}/ai-notes`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error getting AI notes:", error);
      throw error;
    }
  }

  async updateAINotes(logId, data) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/appointment-logs/${logId}/ai-notes`;

    try {
      const response = await axios.put(apiPath, data, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error updating AI notes:", error);
      throw error;
    }
  }

  async getUserTimeline(userId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/users/${userId}/timeline`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching user timeline:", error);
      throw error;
    }
  }

  async askPatientQuestion(userId, question) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/users/${userId}/ask`;

    try {
      const response = await axios.post(apiPath, { question }, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error asking patient question:", error);
      throw error;
    }
  }

  async getPharmacyStats(startDate, endDate) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/prescriptions/stats?startDate=${startDate}&endDate=${endDate}`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching pharmacy stats:", error);
      throw error;
    }
  }

  async getWaitingList(params = {}) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.offset) queryParams.append("offset", params.offset);

    // Add filter parameters
    if (params.status) queryParams.append("status", params.status);
    if (params.searchValue)
      queryParams.append("searchValue", params.searchValue);

    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/waiting-list${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    try {
      const response = await axios.get(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error fetching waiting list:", error);
      throw error;
    }
  }

  async updateWaitingListItem(id, status) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/waiting-list/${id}`;

    try {
      const response = await axios.put(apiPath, { status }, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error updating waiting list item:", error);
      throw error;
    }
  }

  async removeWaitingListItem(id) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/waiting-list/${id}`;

    try {
      const response = await axios.delete(apiPath, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error removing waiting list item:", error);
      throw error;
    }
  }

  async switchPurchaseToAsync(purchaseId) {
    const accessToken = window.localStorage.getItem("accessToken");
    const axiosConfig = defaultAxiosConfig(accessToken);
    const apiPath = `${process.env.REACT_APP_BACKEND_URL}/admin/purchases/${purchaseId}/switch-to-async`;

    try {
      const response = await axios.post(apiPath, {}, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error switching purchase to async:", error);
      throw error;
    }
  }
}

export const customerApi = new CustomerApi();
